import { ReactNode, useCallback } from 'react';
import isEqual from 'lodash/isEqual';

import { CheckableContainer, CheckableGroup } from './checkable-input.style';
import TooltipWrapper from 'approot/shared/tooltip/tooltip-wrapper';

type CheckableVariant = 'pill' | 'button';

type CheckableValue = string | number | object;

type CheckableOption<T extends CheckableValue = CheckableValue> = {
  label: ReactNode;
  value: T;
  isDisabled?: boolean;
  'aria-label'?: string;
  'data-selector'?: string;
  onClick?: (value: T) => void;
  tooltipText?: string;
};

type Props<T extends CheckableValue = CheckableValue> = {
  variant?: CheckableVariant;
  name: string;
  selected: T[];
  options: CheckableOption<T>[];
  canDeselectAll?: boolean;
  isSmall?: boolean;
  isRound?: boolean;
  marginBottom?: number;
  hasError?: boolean;
  onSelect?: (selected: T[], newValue: T) => void;
};

export const CheckableMulti = <T extends CheckableValue = CheckableValue>({
  variant = 'pill',
  name,
  options,
  selected,
  canDeselectAll,
  isSmall,
  isRound,
  marginBottom,
  hasError,
  onSelect,
}: Props<T>) => {
  const handleSelect = useCallback(
    (option: CheckableOption<T>) => {
      if (selected.includes(option.value)) {
        if (!canDeselectAll && selected.length === 1) {
          return;
        }
        onSelect?.(
          selected.filter(s => s !== option.value),
          option.value
        );
      } else {
        onSelect?.([...selected, option.value], option.value);
      }
    },
    [canDeselectAll, selected, onSelect]
  );

  return (
    <CheckableGroup role="group">
      {options.map((option, index) => (
        <CheckableContainer
          key={index}
          $isPill={variant === 'pill'}
          $isRound={isRound}
          $isSmall={isSmall}
          $marginBottom={marginBottom}
          $isDisabled={option.isDisabled}
        >
          <input
            type="checkbox"
            id={`${name}-${index}`}
            name={name}
            checked={selected.includes(option.value)}
            disabled={option.isDisabled}
            aria-invalid={!!hasError}
            aria-describedby={`${name}-error-message`}
            onChange={() =>
              option.onClick
                ? option.onClick(option.value)
                : handleSelect(option)
            }
          />
          <label
            key={index}
            id={`${name}-${index}`}
            htmlFor={`${name}-${index}`}
            aria-label={option['aria-label']}
            data-selector={`checkable-option-${
              option['data-selector'] || option.value
            }`}
          >
            {option.label}
          </label>
        </CheckableContainer>
      ))}
    </CheckableGroup>
  );
};

type SingleProps<T extends CheckableValue = CheckableValue> = {
  variant?: CheckableVariant;
  name: string;
  options: CheckableOption<T>[];
  selected: T;
  isSmall?: boolean;
  isRound?: boolean;
  canWrap?: boolean;
  marginBottom?: number;
  onSelect?: (value: T) => void;
};

export const CheckableSingle = <T extends CheckableValue = CheckableValue>({
  variant = 'pill',
  name,
  options,
  selected,
  isSmall,
  isRound,
  canWrap,
  marginBottom,
  onSelect,
  ...rest
}: SingleProps<T>) => {
  const handleSelect = useCallback(
    (option: CheckableOption<T>) => {
      onSelect?.(option.value);
    },
    [onSelect]
  );

  return (
    <CheckableGroup role="radiogroup" $canWrap={canWrap}>
      {options.map((option, index) => {
        const checkableContent = (
          <CheckableContainer
            key={index}
            $isPill={variant === 'pill'}
            $isRound={isRound}
            $isSmall={isSmall}
            $marginBottom={marginBottom}
            $isDisabled={option.isDisabled}
          >
            <input
              type="radio"
              id={`${name}-${index}`}
              name={name}
              checked={isEqual(selected, option.value)}
              disabled={option.isDisabled}
              onChange={() =>
                option.onClick
                  ? option.onClick(option.value)
                  : handleSelect(option)
              }
              {...rest}
            />
            <label
              key={index}
              id={`${name}-${index}`}
              htmlFor={`${name}-${index}`}
              aria-label={option['aria-label']}
              data-selector={`checkable-option-${
                option['data-selector'] || option.value
              }`}
            >
              {option.label}
            </label>
          </CheckableContainer>
        );

        return option?.tooltipText ? (
          <TooltipWrapper key={index} hoverText={option.tooltipText}>
            {checkableContent}
          </TooltipWrapper>
        ) : (
          checkableContent
        );
      })}
    </CheckableGroup>
  );
};
